import { preFillApplication, toPersistedApplication } from '@/adapter/applicationLoader';
import BlockApi from '@/api/api';
import FolderTreeView from '@/components/navigation/folder/FolderTreeView';
import { useApplication } from '@/hooks/useApplication';
import { useConfig } from '@/hooks/useConfig';
import { useDialogs } from '@/hooks/useDialogs';
import { useMetadata } from '@/hooks/useMetadata';
import { BackendApplication, ReleaseTarget } from '@/types/backend';
import { Folder } from '@/types/folder';
import { mergeFolders } from '@/util/folder';
import { Box, Checkbox, DialogContentText, FormControlLabel } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { ConfirmOptions } from 'material-ui-confirm';
import { User } from 'oidc-client-ts';
import { FC, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';

type SaveApplicationDialogProps = {
  open: boolean;
  onClose: () => void;
  setRecentApplications?: (result: BackendApplication[]) => void;
};

const SaveApplicationDialog: FC<SaveApplicationDialogProps> = (props) => {
  const { config } = useConfig();
  const auth = useAuth();
  const { application, setApplication, setHasUnsavedChanges, backendApplication, setBackendApplication } =
    useApplication();
  const { closeProgress, showProgress, showError, confirm } = useDialogs();
  const { metadata } = useMetadata();

  const [labelText, setLabelText] = useState('');
  const [description, setDescription] = useState('');
  const [folderId, setFolderId] = useState(-1);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [showHelperText, setShowHelperText] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [path, setPath] = useState('');
  const [publishAfterSave, setPublishAfterSave] = useState<boolean>(true);

  const [folders, setFolders] = useState<Folder[]>();

  const handleClose = () => {
    props.onClose();
  };

  const handleError = (error: Error) => {
    showError({
      message: error.message,
      stacktrace: error
    });
  };

  const getFolders = async () => {
    if (!auth?.user) return;
    setIsLoading(true);
    BlockApi(config.apiUrl, auth.user.access_token)
      .folder.list()
      .then(
        (result) => {
          if (!result) return;
          const { rootFolders } = mergeFolders(result);
          setFolders(rootFolders);
          const savePath = backendApplication && getPath(result, backendApplication?.folderId);
          savePath && setPath('/ ' + savePath);

          setIsLoading(false);
        },
        (error) => {
          handleError(error);
        }
      );
  };

  const getPath = (folders: Folder[] | undefined, copyId: number | undefined, result?: string): string | undefined => {
    const selectedFolder = folders && folders[folders.findIndex((folder) => folder.folderId === copyId)];
    if (!selectedFolder) return;

    result = !result ? application?.label : result;
    result = `${selectedFolder?.name} / ` + result;
    if (selectedFolder.parentFolderId) {
      return getPath(folders, selectedFolder.parentFolderId, result);
    } else {
      return result;
    }
  };

  const loadData = () => {
    getFolders();
    if (backendApplication) {
      backendApplication.label && setLabelText(backendApplication.label);
      backendApplication.folderId && setFolderId(backendApplication.folderId);
      backendApplication.name && setName(backendApplication.name);
    } else {
      application?.label && application.label !== 'New Application' && setLabelText(application.label);
      application?.name && setName(application.name);
    }
  };

  const clearData = () => {
    setLabelText('');
    setDescription('');
    setFolderId(-1);
    setName('');
    setComment('');
  };

  const loadLatestApplications = () => {
    auth.user &&
      BlockApi(config.apiUrl, auth.user.access_token)
        .application.latest(5)
        .then(
          (result) => {
            result?.length && props.setRecentApplications && props.setRecentApplications(result);
          },
          (error) => {
            showError({
              title: 'An error has occurred while loading latest used applications',
              message: error.message,
              stacktrace: error
            });
          }
        );
  };

  const handlePublish = async (revisionNumber: number) => {
    if (!auth.user || !backendApplication?.applicationId || backendApplication?.revision === undefined) return;

    await BlockApi(config.apiUrl, auth.user.access_token).release.create(
      backendApplication.applicationId,
      revisionNumber,
      ReleaseTarget.Whitelist
    );
  };

  const handleSave = async () => {
    if (!backendApplication && (!labelText || folderId < 0 || !name)) {
      setShowHelperText(true);
      return;
    }
    if (!auth?.user) return;

    if (backendApplication?.applicationId && application.applicationId !== backendApplication.applicationId) {
      showError({
        title: 'Application mismatch',
        message: `The loaded application (${application.applicationId}) is not the one this would save to (${backendApplication.applicationId}). Reload the application before saving.`
      });
      return;
    }

    showProgress('Saving Application...');
    if (backendApplication?.applicationId && isCurrentCompany) {
      saveApplication(backendApplication, auth.user);
    } else {
      saveNewApplication(auth.user);
    }
  };

  const saveNewApplication = (user: User) => {
    const savedApplication = toPersistedApplication({ ...application, generated: new Date().toISOString() });
    BlockApi(config.apiUrl, user.access_token)
      .application.create(name, labelText, description, folderId, JSON.stringify(savedApplication), comment)
      .then(
        (result) => {
          BlockApi(config.apiUrl, user.access_token)
            .application.load(result.applicationId, result.revision)
            .then(
              (result) => {
                if (result) {
                  console.info('Success! Application loaded successfully', result);
                  const { blockData, ...backendApp } = result;
                  const newApp = JSON.parse(blockData);
                  const prefilledApp = preFillApplication(newApp, metadata);

                  // set second inparameter to true for the setApplication context
                  //  to set hasUnsavedChanges to false
                  application &&
                    setApplication(
                      {
                        ...prefilledApp,
                        applicationId: backendApp.applicationId,
                        revision: backendApp.revision
                      },
                      true
                    );
                  setBackendApplication(backendApp);
                  loadLatestApplications();
                  setHasUnsavedChanges(false);
                  closeProgress();
                  handleClose();
                } else {
                  showError({
                    title: 'An error occured',
                    message: 'Unable to load the application after saving'
                  });
                }
              },
              (error) => {
                handleError(error);
              }
            );
        },
        (error) => {
          handleError(error);
        }
      );
  };

  const saveApplication = (backendApplication: BackendApplication, user: User) => {
    if (!backendApplication.applicationId) return;

    const savedApplication = toPersistedApplication({ ...application, generated: new Date().toISOString() });
    BlockApi(config.apiUrl, user.access_token)
      .application.save(backendApplication.applicationId, JSON.stringify(savedApplication), comment)
      .then((revisionNumber) => {
        publishAfterSave && handlePublish(revisionNumber);
        setBackendApplication({ ...backendApplication, revision: revisionNumber });
        //set second inparameter to true for the setApplication context to set hasUnsavedChanges to false
        application && setApplication({ ...application, revision: revisionNumber }, true);
        loadLatestApplications();
        setPublishAfterSave(true);
        setHasUnsavedChanges(false);
        closeProgress();
        handleClose();
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const isCurrentCompany =
    !backendApplication?.companyId || backendApplication.companyId === auth.user?.profile.company_id;

  const confirmSave: ConfirmOptions = {
    title: 'Company has changed',
    description:
      'The company this application was loaded from is different from the current company. Do you still wish to save the application?',
    confirmationText: 'Yes',
    cancellationButtonProps: { color: 'secondary' }
  };

  useEffect(() => {
    if (props.open) {
      loadData();
      if (!isCurrentCompany) {
        confirm(confirmSave).then((result) => {
          if (!result.confirmed) handleClose();
        });
      }
    } else if (!props.open) {
      clearData();
      setShowHelperText(false);
    }
  }, [props.open]);

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth='xs' disableRestoreFocus>
      <DialogTitle>Save{!backendApplication && ` new`} Application</DialogTitle>
      <DialogContent>
        <Box component='form' sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {(!backendApplication || !isCurrentCompany) && (
            <>
              <FolderTreeView
                required
                loading={isLoading}
                disabled={!!backendApplication}
                title='Select folder'
                error={showHelperText && folderId < 0}
                helperText={showHelperText && folderId < 0 ? 'Folder is required' : ''}
                folders={folders}
                onFolderClick={(folder) => setFolderId(folder.folderId)}
                onFolderDoubleClick={(folder) => setFolderId(folder.folderId)}
              />
              <TextField
                required
                error={showHelperText && !labelText}
                helperText={showHelperText && !labelText ? 'Label is required' : ''}
                autoFocus={props.open}
                label='Label'
                variant='outlined'
                color='primary'
                size='small'
                value={labelText}
                onChange={(e) => setLabelText(e.target.value)}
                disabled={!!backendApplication}
                InputProps={{ autoComplete: 'off' }}
              />
              <TextField
                required
                error={showHelperText && !name}
                helperText={showHelperText && !name ? 'Name is required' : ''}
                label='Name'
                variant='outlined'
                color='primary'
                size='small'
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!!backendApplication}
                InputProps={{ autoComplete: 'off' }}
              />
              <TextField
                error={showHelperText && !labelText}
                label='Description'
                variant='outlined'
                color='primary'
                size='small'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!!backendApplication}
                InputProps={{ autoComplete: 'off' }}
              />
            </>
          )}
          {backendApplication && <DialogContentText>{path}</DialogContentText>}
          <TextField
            label='Comment (optional)'
            variant='outlined'
            color='primary'
            size='small'
            multiline
            minRows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            InputProps={{ autoComplete: 'off' }}
          />
          {backendApplication && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={publishAfterSave}
                  onChange={() => setPublishAfterSave(!publishAfterSave)}
                  color='default'
                />
              }
              label='Publish to whitelist'
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} color='primary'>
          Cancel
        </Button>
        <Button onClick={handleSave} color='primary'>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveApplicationDialog;
